<template>
  <div class="min-h-screen bg-slate-900 text-white">
    <div class="max-w-6xl mx-auto px-6 py-10">
      <div class="flex flex-col gap-6">
        <div>
          <h1 class="text-3xl font-semibold">
            Admin countries
          </h1>
          <p class="text-slate-300 mt-2">
            Ajoute des entrées et télécharge un nouveau fichier
            <span class="font-mono">countries.json</span>.
          </p>
        </div>

        <div class="grid gap-6 lg:grid-cols-[1.2fr_1fr]">
          <div class="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div class="flex items-center justify-between mb-3">
              <h2 class="text-lg font-semibold">
                Aperçu sur le globe
              </h2>
              <span
                class="text-sm text-slate-400"
                v-if="selectedEntry"
              >
                {{ selectedEntry.cities.title }}
              </span>
            </div>
            <div
              ref="mapContainer"
              class="h-72 rounded-lg border border-slate-700"
            />
            <div class="mt-3 flex flex-wrap items-center gap-2">
              <input
                v-model="searchQuery"
                class="flex-1 min-w-[180px] rounded-md bg-slate-800 border border-slate-600 px-3 py-2 text-black placeholder-slate-400 caret-white"
                placeholder="Chercher une ville ou un lieu"
                @keyup.enter="searchLocation"
              >
              <button
                class="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-md"
                :disabled="searchLoading"
                @click="searchLocation"
              >
                Chercher
              </button>
            </div>
            <p
              v-if="searchStatus"
              class="text-xs mt-2"
              :class="searchStatusClass"
            >
              {{ searchStatus }}
            </p>
            <p class="text-xs text-slate-400 mt-2">
              Clique sur une ligne pour positionner le marqueur. Déplace-le pour ajuster la latitude/longitude.
            </p>
          </div>
          <div class="bg-slate-800 rounded-xl p-6 border border-slate-700">
            <div class="flex flex-wrap items-center gap-3 mb-4">
              <button
                class="bg-slate-700 hover:bg-slate-600 text-white px-4 py-2 rounded-md"
                :disabled="loading"
                @click="loadCountries"
              >
                Recharger
              </button>
              <button
                class="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-md"
                :disabled="loading"
                @click="addEntry"
              >
                Ajouter une entrée
              </button>
              <button
                class="bg-indigo-500 hover:bg-indigo-600 text-white px-4 py-2 rounded-md"
                :disabled="loading"
                @click="downloadJson"
              >
                Télécharger countries.json
              </button>
              <span
                class="text-sm text-slate-400"
                v-if="sourceUrl"
              >
                Source: {{ sourceUrl }}/countries.json
              </span>
            </div>

            <div
              v-if="status"
              class="mb-4 text-sm"
              :class="statusClass"
            >
              {{ status }}
            </div>

            <div class="overflow-x-auto">
              <table class="min-w-full text-sm">
                <thead class="text-slate-300">
                  <tr>
                    <th class="text-left py-2">
                      Code pays
                    </th>
                    <th class="text-left py-2">
                      Ville
                    </th>
                    <th class="text-left py-2">
                      Latitude
                    </th>
                    <th class="text-left py-2">
                      Longitude
                    </th>
                    <th class="text-left py-2">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(entry, index) in entries"
                    :key="index"
                    class="border-t border-slate-700 cursor-pointer transition-colors hover:bg-indigo-500"
                    :class="{
                      'bg-indigo-600 ring-1 ring-slate-500': index === selectedIndex
                    }"
                    @click="selectEntry(index)"
                  >
                    <td class="py-2 pr-2">
                      <input
                        v-model="entry.key"
                        class="w-20 rounded-md bg-slate-800 border border-slate-600 px-2 py-1 text-black placeholder-slate-400 caret-white"
                        placeholder="FR"
                      >
                    </td>
                    <td class="py-2 pr-2">
                      <input
                        v-model="entry.cities.title"
                        class="w-48 rounded-md bg-slate-800 border border-slate-600 px-2 py-1 text-black placeholder-slate-400 caret-white"
                        placeholder="Paris"
                      >
                    </td>
                    <td class="py-2 pr-2">
                      <input
                        v-model.number="entry.cities.latitude"
                        type="number"
                        step="0.01"
                        class="w-28 rounded-md bg-slate-800 border border-slate-600 px-2 py-1 text-black placeholder-slate-400 caret-white"
                        placeholder="48.85"
                      >
                    </td>
                    <td class="py-2 pr-2">
                      <input
                        v-model.number="entry.cities.longitude"
                        type="number"
                        step="0.01"
                        class="w-28 rounded-md bg-slate-800 border border-slate-600 px-2 py-1 text-black placeholder-slate-400 caret-white"
                        placeholder="2.35"
                      >
                    </td>
                    <td class="py-2">
                      <button
                        class="text-red-300 hover:text-red-200"
                        @click="removeEntry(index)"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mapboxgl from 'mapbox-gl/dist/mapbox-gl.js';
import 'mapbox-gl/dist/mapbox-gl.css';

export default {
  name: 'AdminCountries',
  data() {
    return {
      sourceUrl: (process.env.VUE_APP_S3_CONFIG_BASE_URL || '').replace(/\/+$/, ''),
      entries: [],
      loading: false,
      status: '',
      statusType: 'info',
      map: null,
      marker: null,
      selectedIndex: null,
      searchQuery: '',
      searchStatus: '',
      searchStatusType: 'info',
      searchLoading: false
    };
  },
  computed: {
    statusClass() {
      return this.statusType === 'error' ? 'text-red-400' : 'text-emerald-300';
    },
    searchStatusClass() {
      return this.searchStatusType === 'error' ? 'text-red-400' : 'text-emerald-300';
    },
    selectedEntry() {
      if (this.selectedIndex === null) {
        return null;
      }
      return this.entries[this.selectedIndex] || null;
    }
  },
  mounted() {
    this.loadCountries();
    this.initMap();
  },
  methods: {
    initMap() {
      if (!this.$refs.mapContainer) {
        return;
      }

      mapboxgl.accessToken = process.env.VUE_APP_MAPBOX_TOKEN || 'YOUR_MAPBOX_ACCESS_TOKEN';

      this.map = new mapboxgl.Map({
        container: this.$refs.mapContainer,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: [0, 20],
        zoom: 1.3,
        projection: 'globe'
      });

      this.map.on('load', () => {
        this.map.setFog({
          range: [0.5, 10],
          color: '#1a1a2e',
          'horizon-blend': 0.2
        });
      });
    },
    async loadCountries() {
      this.loading = true;
      this.setStatus('Chargement...', 'info');

      try {
        if (!this.sourceUrl) {
          this.entries = [];
          this.setStatus('Aucune URL de config S3 définie.', 'error');
          return;
        }

        const response = await fetch(`${this.sourceUrl}/countries.json`);
        if (!response.ok) {
          throw new Error('Impossible de charger countries.json.');
        }

        const data = await response.json();
          this.entries = Array.isArray(data?.countries)
            ? data.countries
            : [];
        if (this.entries.length && this.selectedIndex === null) {
          this.selectEntry(0);
        }
        this.setStatus('Données chargées.', 'info');
      } catch (error) {
        this.entries = [];
        this.setStatus(error.message || 'Erreur de chargement.', 'error');
      } finally {
        this.loading = false;
      }
    },
    addEntry() {
      this.entries.unshift({
        key: '',
        cities: {
          title: '',
          latitude: 0,
          longitude: 0
        }
      });
      this.selectEntry(0);
    },
    removeEntry(index) {
      this.entries.splice(index, 1);
      if (this.selectedIndex === index) {
        this.selectedIndex = this.entries.length ? 0 : null;
        if (this.selectedIndex !== null) {
          this.updateMarkerFromEntry(this.entries[this.selectedIndex]);
        } else if (this.marker) {
          this.marker.remove();
          this.marker = null;
        }
      }
    },
    selectEntry(index) {
      this.selectedIndex = index;
      const entry = this.entries[index];
      if (entry) {
        this.updateMarkerFromEntry(entry);
      }
    },
    updateMarkerFromEntry(entry) {
      if (!this.map || !entry?.cities) {
        return;
      }
      const { longitude, latitude } = entry.cities;
      if (typeof longitude !== 'number' || typeof latitude !== 'number') {
        return;
      }

      const position = [longitude, latitude];

      if (!this.marker) {
        this.marker = new mapboxgl.Marker({ draggable: true })
          .setLngLat(position)
          .addTo(this.map);

        this.marker.on('dragend', () => {
          const lngLat = this.marker.getLngLat();
          if (this.selectedIndex !== null && this.entries[this.selectedIndex]) {
            this.entries[this.selectedIndex].cities.longitude = Number(lngLat.lng.toFixed(5));
            this.entries[this.selectedIndex].cities.latitude = Number(lngLat.lat.toFixed(5));
          }
        });
      } else {
        this.marker.setLngLat(position);
      }

      this.map.flyTo({ center: position, zoom: 3, duration: 800 });
    },
    async searchLocation() {
      const query = this.searchQuery.trim();
      if (!query) {
        this.setSearchStatus('Renseigne une recherche.', 'error');
        return;
      }

      if (!this.map) {
        this.setSearchStatus('Carte non prête.', 'error');
        return;
      }

      const token = process.env.VUE_APP_MAPBOX_TOKEN || mapboxgl.accessToken;
      if (!token || token === 'YOUR_MAPBOX_ACCESS_TOKEN') {
        this.setSearchStatus('Token Mapbox manquant.', 'error');
        return;
      }

      this.searchLoading = true;
      this.setSearchStatus('Recherche en cours...', 'info');

      try {
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?limit=1&access_token=${token}`
        );
        if (!response.ok) {
          throw new Error('Recherche impossible.');
        }
        const payload = await response.json();
        const feature = payload.features?.[0];
        if (!feature) {
          this.setSearchStatus('Aucun résultat.', 'error');
          return;
        }

        const [longitude, latitude] = feature.center;
        const countryContext = feature.context?.find((item) => item.id?.startsWith('country'));
        const entry = this.selectedEntry;
        if (entry) {
          if (countryContext) {
            if (countryContext.short_code) {
              entry.key = countryContext.short_code.toUpperCase();
            }
          }
          entry.cities.title = feature.text || entry.cities.title;
          entry.cities.longitude = Number(longitude.toFixed(5));
          entry.cities.latitude = Number(latitude.toFixed(5));
        }
        this.updateMarkerFromEntry({ cities: { longitude, latitude } });
        this.setSearchStatus(`Trouvé: ${feature.place_name}`, 'info');
      } catch (error) {
        this.setSearchStatus(error.message || 'Erreur de recherche.', 'error');
      } finally {
        this.searchLoading = false;
      }
    },
    downloadJson() {
      const payload = {
        countries: this.entries.map((entry) => ({
          key: entry.key,
          cities: entry.cities
        }))
      };
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: 'application/json'
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'countries.json';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
    setStatus(message, type) {
      this.status = message;
      this.statusType = type;
    },
    setSearchStatus(message, type) {
      this.searchStatus = message;
      this.searchStatusType = type;
    }
  }
};
</script>
